import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from 'components/AppIcon';
import {
  isSupabaseConfigured,
  isDemoModeForced,
  hasRealSupabaseCreds,
  clearDemoOverride,
} from 'lib/supabase';

const DISMISS_KEY = 'edutracker.demoBannerDismissed.v1';

// One banner, three states:
//   1. No Supabase creds AT ALL         → hidden if dismissed; otherwise
//                                          "connect Supabase" prompt.
//   2. Creds exist, `?demo=1` override  → yellow "you're viewing the demo"
//                                          bar with an *Exit demo mode* link.
//   3. Fully live                       → nothing renders.
//
// The point of state (2) is so a live production URL can double as a sales
// demo: your rep loads `yourdomain.com/?demo=1`, the app switches to
// localStorage-only, no real school data is touched, and this banner makes
// the current mode obvious to anyone in the room.
const DemoModeBanner = () => {
  const [dismissed, setDismissed] = useState(false);
  const [, forceRerender] = useState(0);
  const location = useLocation();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      setDismissed(window.sessionStorage.getItem(DISMISS_KEY) === '1');
    } catch {
      setDismissed(false);
    }
  }, []);

  if (isSupabaseConfigured()) {
    return null;
  }

  // Never render this banner on the Go Live page itself — the whole page
  // IS the "you're not live yet, here's how to fix it" experience, and
  // stacking a banner about it on top of it looks broken.
  if (location?.pathname === '/go-live' || location?.pathname === '/setup') {
    return null;
  }

  const forced = isDemoModeForced() && hasRealSupabaseCreds();

  if (!forced && dismissed) {
    return null;
  }

  const handleDismiss = () => {
    try {
      window.sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // Best-effort — still hide it for this render.
    }
    setDismissed(true);
  };

  const handleExitDemo = () => {
    clearDemoOverride();
    forceRerender((n) => n + 1);
    // Also clear the demo userSession so the app doesn't keep the
    // visitor "signed in" as the fake Therapist/Teacher after they
    // land back on the login page — otherwise the router bounces
    // them right back into the demo view.
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.removeItem('userSession');
      } catch {
        // Storage errors are non-fatal — the redirect below still
        // pulls the user out of the demo view.
      }
      // Send them to the sign-in page instead of just stripping the
      // query param off the current URL. The banner CTA reads "Sign
      // in with a real account →", so this is where they expect to
      // land.
      const base = window.location.origin;
      window.location.href = `${base}/login?live=1`;
    }
  };

  if (forced) {
    // Copy note: this banner shows on top of the app during a demo,
    // where a district evaluator can see it. It should read like a
    // friendly tour message — never like an error. Absolutely no
    // developer language ("?demo=1", "local-only mode", "Supabase
    // credentials") — that terrified a stakeholder in the past.
    return (
      <div
        role="status"
        className="w-full bg-blue-50 border-b border-blue-200 text-blue-900"
      >
        <div className="max-w-7xl mx-auto flex items-start gap-3 px-4 py-2 text-sm">
          <Icon
            name="Eye"
            size={16}
            className="mt-0.5 flex-shrink-0 text-blue-700"
          />
          <div className="flex-1">
            <span className="font-medium">You're exploring the demo.</span>{' '}
            <span>
              Click around, add students, log data — none of it saves to
              a real account. Ready to use this for your actual caseload?
            </span>
          </div>
          <button
            type="button"
            onClick={handleExitDemo}
            className="ml-2 flex-shrink-0 rounded px-3 py-1 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            Sign in with a real account →
          </button>
        </div>
      </div>
    );
  }

  // Teacher-facing copy first, tech-coordinator detail behind a toggle
  // — same pattern as SchemaSetupBanner. Nobody in a classroom needs to
  // read "VITE_SUPABASE_ANON_KEY as environment variables" during a
  // planning period; that's what the "How to switch to live mode"
  // details link is for.
  return <DemoModeExplainerBanner onDismiss={handleDismiss} />;
};

const DemoModeExplainerBanner = ({ onDismiss }) => {
  const [showTech, setShowTech] = useState(false);
  return (
    <div
      role="status"
      className="w-full bg-blue-50 border-b border-blue-200 text-blue-900"
    >
      <div className="max-w-7xl mx-auto flex items-start gap-3 px-4 py-2 text-sm">
        <Icon
          name="Eye"
          size={16}
          className="mt-0.5 flex-shrink-0 text-blue-700"
        />
        <div className="flex-1 min-w-0">
          <p>
            <span className="font-medium">You're exploring the demo.</span>{' '}
            <span>
              Click anywhere, add students, log data — this is a safe
              practice space and nothing is saved to a real account.
              When you're ready to use this for your actual caseload,
              use the button below.
            </span>
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <Link
              to="/go-live"
              className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              <Icon name="Rocket" size={12} />
              Set up for real use
            </Link>
            <button
              type="button"
              onClick={() => setShowTech((v) => !v)}
              className="inline-flex items-center gap-1 rounded px-1 py-1 text-xs font-medium text-blue-800 hover:text-blue-900 underline underline-offset-2"
            >
              {showTech ? 'Hide' : 'For your tech coordinator'}
            </button>
          </div>
          {showTech && (
            <div className="mt-2 rounded-md border border-blue-200 bg-white/70 p-3 text-xs text-blue-900">
              <p>
                To connect a real shared database, add{' '}
                <code className="font-mono">VITE_SUPABASE_URL</code> and{' '}
                <code className="font-mono">VITE_SUPABASE_ANON_KEY</code>{' '}
                as environment variables on your host (Cloudflare Pages
                → Settings → Environment variables), then redeploy.
                Full walkthrough on the{' '}
                <Link
                  to="/go-live"
                  className="underline underline-offset-2 hover:text-blue-950"
                >
                  Go Live page
                </Link>
                .
              </p>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss demo notice"
          className="ml-2 flex-shrink-0 rounded p-1 text-blue-700 hover:bg-blue-100 hover:text-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-400"
        >
          <Icon name="X" size={14} />
        </button>
      </div>
    </div>
  );
};

export default DemoModeBanner;
